import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

const NursingRecordForm = ({ formData, handleChange, rows, setRows, nurses }) => {
    const { user, roles } = useSelector((state) => state.auth);

    const isNurseRole = (Array.isArray(roles) && roles.some(r => typeof r === 'string' && r.toLowerCase().includes('nurse'))) || (user?.role && String(user.role).toLowerCase().includes('nurse'));
    const matchingNurse = nurses && user ? nurses.find(n => String(n.id) === String(user.id) || n.name?.toLowerCase() === user.name?.toLowerCase()) : null;
    
    const shouldLockNurse = isNurseRole || !!matchingNurse;
    const loggedInNurseId = matchingNurse ? matchingNurse.id : (user?.id || "");
    const loggedInNurseName = matchingNurse ? matchingNurse.name : (user?.name || "");

    useEffect(() => {
        if (shouldLockNurse && loggedInNurseId && rows && rows.length > 0) {
            let updated = false;
            const newRows = rows.map((r) => {
                // Only update nurse/nurse_id for new rows (i.e. those without a created_at timestamp)
                if (!r.created_at && (!r.nurse_id || String(r.nurse_id) !== String(loggedInNurseId))) {
                    updated = true;
                    return {
                        ...r,
                        nurse_id: loggedInNurseId,
                        nurse: loggedInNurseName,
                    };
                }
                return r;
            });
            if (updated) {
                setRows(newRows);
            }
        }
    }, [shouldLockNurse, loggedInNurseId, loggedInNurseName, rows]);

    const safeRows = rows || [];

    const addRow = () => {
        setRows([
            {
                id: Date.now(),
                date: new Date().toISOString().split('T')[0],
                hours: "",
                remarks: "",
                nurse: shouldLockNurse ? loggedInNurseName : "",
                nurse_id: shouldLockNurse ? loggedInNurseId : null,
            },
            ...rows,
        ]);
    };

    const removeRow = (id) => {
        if (rows.length === 1) return;
        Swal.fire({
            title: "Delete this entry?",
            text: "This record will be removed.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it",
        }).then((res) => {
            if (res.isConfirmed) {
                setRows(rows.filter((r) => r.id !== id));
            }
        });
    };

    const handleRowChange = (id, field, value) => {
        if (field === "nurse") {
            // Find the selected nurse object from the dynamic list
            const selectedNurse = nurses.find(n => String(n.id) === String(value));

            setRows(rows.map((r) =>
                r.id === id
                    ? {
                        ...r,
                        nurse: selectedNurse ? selectedNurse.name : "", // Name store hoga
                        nurse_id: selectedNurse ? selectedNurse.id : null // ID store hogi
                    }
                    : r
            ));
        } else {
            setRows(rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
        }
    };

    return (
        <div className="p-4">
            {/* ==== Basic Info ==== */}
            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="name"
                            className="form-control"
                            placeholder="Name"
                            value={formData.name || ""}
                            onChange={handleChange}
                            readOnly={!!formData.patientId || !!formData.patient_id}
                        />
                        <label>Name</label>
                    </div>
                </div>
                <div className="col-md-2">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="mrNo"
                            className="form-control"
                            placeholder="M.R No."
                            value={formData.mrNo || ""}
                            onChange={handleChange}
                        />
                        <label>M.R No.</label>
                    </div>
                </div>
                <div className="col-md-2">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="roomNo"
                            className="form-control"
                            placeholder="Room No."
                            value={formData.roomNo || ""}
                            onChange={handleChange}
                        />
                        <label>Room No.</label>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            name="consultant"
                            className="form-control"
                            placeholder="Consultant"
                            value={formData.consultant || ""}
                            onChange={handleChange}
                        />
                        <label>Consultant</label>
                    </div>
                </div>
            </div>

            {/* ==== Repeater Rows ==== */}
            <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
                <small className="text-dark bg-primary-subtle rounded-4 fw-bold mb-0 px-3 py-1 flex-grow-1 me-3">
                    Daily Nursing Record
                </small>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={addRow}>
                    <i className="ri-add-line me-1"></i> Add Row
                </button>
            </div>

            {safeRows.map((row) => (
                <div key={row.id} className="border rounded-4 p-3 mb-3 bg-white shadow-sm">
                    {/* --- First Row: Date, Hours, Nurse --- */}
                    <div className="row g-3 mb-3">
                        {/* Date Field */}
                        <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="date"
                                    className="form-control"
                                    id={`date_${row.id}`}
                                    value={row.date}
                                    onChange={(e) => handleRowChange(row.id, "date", e.target.value)}
                                />
                                <label htmlFor={`date_${row.id}`}>Date</label>
                            </div>
                        </div>

                        {/* Hours Field */}
                        <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    id={`hours_${row.id}`}
                                    placeholder="Hours"
                                    value={row.hours}
                                    onChange={(e) => handleRowChange(row.id, "hours", e.target.value)}
                                />
                                <label htmlFor={`hours_${row.id}`}>Hours</label>
                            </div>
                        </div>

                        {/* Nurse Selection - Takes remaining 6 columns to fill the row */}
                        <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                                <select
                                    className="form-select"
                                    id={`nurse_${row.id}`}
                                    value={row.nurse_id || ""} // value mein ID use karein
                                    onChange={(e) => handleRowChange(row.id, "nurse", e.target.value)}
                                    disabled={shouldLockNurse}
                                >
                                    <option value="">Select Nurse</option>
                                    {nurses && nurses.map((nurse) => (
                                        <option key={nurse.id} value={nurse.id}> {/* value ID honi chahiye */}
                                            {nurse.name}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor={`nurse_${row.id}`}>Select Nurse</label>
                            </div>
                        </div>
                    </div>

                    {/* --- Second Row: Remarks and Delete --- */}
                    <div className="row g-3 align-items-end">
                        {/* Remarks Field - Changed to Textarea */}
                        <div className="col-md-11">
                            <div className="form-floating form-floating-outline">
                                <textarea
                                    className="form-control"
                                    id={`remarks_${row.id}`}
                                    placeholder="Remarks"
                                    style={{ height: '80px' }}
                                    value={row.remarks}
                                    onChange={(e) => handleRowChange(row.id, "remarks", e.target.value)}
                                />
                                <label htmlFor={`remarks_${row.id}`}>Remarks</label>
                            </div>
                        </div>

                        {/* Delete Button - Shifted to second row end */}
                        <div className="col-md-1 text-end">
                            <button
                                type="button"
                                className="btn btn-icon btn-outline-danger"
                                style={{ marginBottom: '5px' }} // Aligning with the bottom of the textarea
                                onClick={() => removeRow(row.id)}
                            >
                                <i className="ri-delete-bin-line"></i>
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default NursingRecordForm;
